import { useMemo } from 'react';

import {
	type GenerateSearchUrlType,
	type NavigateCallback,
	type OnNavigate,
} from '../../../common/ui/app-context';
import { isQuickFindAsyncPreloadEnabled } from '../../../common/utils/experiments';
import { isOnSearchRoute, isSearchRouteUrl } from '../../../common/utils/url';
import { useWarmUpSearchPageQuery } from '../../../controllers/quick-find/warmup';

/**
 * Wraps the host-provided `onNavigate` so that — when the
 * `rovo_search_preload_from_quick_find_using_async` experiment is enabled —
 * a navigation toward the search page first kicks off the search query warm-up
 * to prime the backend persisted-query cache.
 *
 * Behaviour notes:
 *
 * - When the experiment parameter `isEnabled` is `false` (or the experiment is
 *   not enrolled), the original `onNavigate` is returned **by identity** so the
 *   false cohort runs bit-for-bit identical code to today.
 * - When enabled, warm-up only fires when `isOnSearchRoute(generateSearchUrl)`
 *   returns `false` (i.e. we're definitively not already on the search page)
 *   AND the destination URL resolves to the search page route
 *   (`isSearchRouteUrl(url, generateSearchUrl) === true`). `true` (already on
 *   the search page) and `undefined` (route lookup failed) both skip warm-up,
 *   as do navigations whose destination is not the search page.
 */
export const useQuickFindOnNavigateWithWarmup = (
	originalOnNavigate: OnNavigate | undefined,
	generateSearchUrl: GenerateSearchUrlType | undefined,
): OnNavigate | undefined => {
	const warmUp = useWarmUpSearchPageQuery();

	const isEnabled = useMemo<boolean>(() => isQuickFindAsyncPreloadEnabled(), []);

	return useMemo(() => {
		// Patch is a no-op when the experiment is off — return original by
		// identity so behaviour is unchanged for the false cohort.
		if (!isEnabled || !originalOnNavigate) {
			return originalOnNavigate;
		}

		const original = originalOnNavigate;
		const wrappedCallback: NavigateCallback = (url, action, event) => {
			// Only warm up when we definitively know we're NOT already on the
			// search page AND the destination is the search page itself. For
			// `isOnSearchRoute`, both `true` (already there) and `undefined`
			// (couldn't tell) skip warm-up.
			if (
				generateSearchUrl &&
				isOnSearchRoute(generateSearchUrl) === false &&
				isSearchRouteUrl(url, generateSearchUrl) === true
			) {
				warmUp(url);
			}

			return typeof original === 'function'
				? original(url, action, event)
				: original.callback(url, action, event);
		};

		return typeof original === 'function'
			? wrappedCallback
			: { callback: wrappedCallback, options: original.options };
	}, [isEnabled, originalOnNavigate, generateSearchUrl, warmUp]);
};
